import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { ExpenseBankSelect } from "@/components/expenses/ExpenseBankSelect";
import {
  PaymentMethodFieldsGroup,
  emptyPaymentMethodDraft,
  paymentMethodUsesBank,
  paymentReferenceForSubmit,
  type PaymentMethodDraft,
} from "@/components/sales/PaymentMethodFields";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatCurrency, getActiveCompanyCurrency } from "@/lib/currencies/currenciesStore";
import { pkrAmountShortLabel } from "@/lib/formatPkrAmount";
import { fetchBanks, type BankOption } from "@/lib/sales/banksStore";
import type { SaleBalancePaymentPayload, SalePaymentMethod } from "@/lib/sales/salesStore";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Calculator, CheckCircle2, HandCoins, Info, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  outstanding: number;
  onSubmit: (payload: SaleBalancePaymentPayload) => Promise<void>;
  title?: string;
  description?: string;
  balanceLabel?: string;
  defaultNoteText?: string;
};

function todayIso(): string {
  return format(new Date(), "yyyy-MM-dd");
}

function parseAmount(raw: string): number | null {
  const trimmed = raw.trim().replace(/,/g, "");
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100) / 100;
}

function formatMoney(n: number): string {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Records a lump-sum or partial balance/down payment on a sale. */
export function RecordBalancePaymentDialog({
  open,
  onOpenChange,
  outstanding,
  onSubmit,
  title,
  description,
  balanceLabel,
  defaultNoteText,
}: Props) {
  const [amountInput, setAmountInput] = useState("");
  const [paymentDate, setPaymentDate] = useState(todayIso());
  const [methodDraft, setMethodDraft] = useState<PaymentMethodDraft>(emptyPaymentMethodDraft("cash"));
  const [banks, setBanks] = useState<BankOption[]>([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    fetchBanks().then(setBanks).catch(() => {});
    setAmountInput(outstanding > 0 ? String(outstanding) : "");
    setPaymentDate(todayIso());
    setMethodDraft(emptyPaymentMethodDraft("cash"));
    setNotes("");
    setSaving(false);
    setError(null);
  }, [open, outstanding]);

  const paymentAmount = parseAmount(amountInput) ?? 0;
  const enteredAmountLabel = pkrAmountShortLabel(paymentAmount);
  const remainingAfterPayment = Math.max(0, Math.round((outstanding - paymentAmount) * 100) / 100);
  const percentCovered =
    outstanding > 0 ? Math.min(100, Math.round((paymentAmount / outstanding) * 100)) : 0;
  const isFullySettled = paymentAmount > 0 && remainingAfterPayment === 0;

  const applyPreset = (percent: number) => {
    if (outstanding <= 0) return;
    const next = Math.round(outstanding * (percent / 100) * 100) / 100;
    setAmountInput(String(next));
    setError(null);
  };

  const save = async () => {
    if (saving) return;
    const amount = parseAmount(amountInput);
    if (amount == null) {
      setError("Enter a valid payment amount greater than zero.");
      return;
    }
    if (amount > outstanding + 0.009) {
      setError(`Payment cannot exceed the outstanding balance of ${formatMoney(outstanding)}.`);
      return;
    }
    if (!paymentDate.trim()) {
      setError("Payment date is required.");
      return;
    }
    if (!methodDraft.paymentMethod) {
      setError("Select a payment method.");
      return;
    }
    if (paymentMethodUsesBank(methodDraft.paymentMethod) && !methodDraft.bankId?.trim()) {
      setError("Select a bank for this payment method.");
      return;
    }

    const isFull = amount >= outstanding - 0.009;
    const mode = isFull ? "full" : "partial";
    const defaultNote = defaultNoteText || (isFull ? "Full Payment" : "Partial Payment");
    const finalNotes = notes.trim() || defaultNote;

    const payments = [
      {
        amount,
        payment_method: (methodDraft.paymentMethod as SalePaymentMethod) || null,
        bank_id: methodDraft.bankId?.trim() ? Number(methodDraft.bankId) : null,
        transaction_reference_no: paymentReferenceForSubmit(methodDraft),
        pay_order_no: methodDraft.paymentMethod === "pay_order" ? methodDraft.payOrderNo?.trim() || null : null,
        payment_mode: mode as "full" | "partial",
        notes: finalNotes,
        payment_date: paymentDate,
      },
    ];

    const primary = payments[0];

    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        amount,
        payment_date: paymentDate,
        payments,
        payment_method: primary?.payment_method ?? null,
        bank_id: primary?.bank_id ?? null,
        transaction_reference_no: primary?.transaction_reference_no ?? null,
        pay_order_no: primary?.pay_order_no ?? null,
        notes: finalNotes,
      });
      onOpenChange(false);
    } catch {
      // Caller shows toast; keep dialog open for correction.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto p-6">
        <DialogHeader className="pb-3 border-b border-border/50">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-600">
              <HandCoins className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-foreground">
                {title ?? "Record Balance Payment"}
              </DialogTitle>
              <p className="text-xs text-muted-foreground">
                {description ?? "Record the full outstanding balance or a partial payment against this sale."}
              </p>
            </div>
          </div>
        </DialogHeader>

        {/* Outstanding Balance Banner */}
        <div className="flex items-center justify-between bg-muted/30 border border-border/50 rounded-xl p-3 text-xs">
          <div className="min-w-0">
            <p className="font-bold text-foreground">{balanceLabel ?? "Sale balance"}</p>
            <p className="text-[11px] text-muted-foreground uppercase tracking-wider font-semibold">
              Outstanding amount due
            </p>
          </div>
          <div className="text-right shrink-0 pl-3">
            <p className="text-[10px] uppercase font-semibold text-muted-foreground">Outstanding</p>
            <p className="font-bold text-primary text-sm">{formatCurrency(outstanding)}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
          {/* Left Column: Form Fields */}
          <div className="space-y-4">
            {/* Amount */}
            <div className="space-y-2">
              <div className="flex min-h-5 items-center justify-between gap-2">
                <Label htmlFor="balance-payment-amount" className="text-xs font-semibold text-foreground">
                  Payment amount ({getActiveCompanyCurrency().code}) <span className="text-destructive">*</span>
                </Label>
                {enteredAmountLabel ? (
                  <span className="shrink-0 text-xs font-bold tabular-nums text-primary">{enteredAmountLabel}</span>
                ) : null}
              </div>
              <div className="relative">
                <Input
                  id="balance-payment-amount"
                  type="text"
                  inputMode="decimal"
                  value={amountInput}
                  disabled={saving}
                  className="glass-input h-10 pr-12 font-semibold"
                  onChange={(e) => {
                    const nextInput = e.target.value;
                    const nextAmount = parseAmount(nextInput);

                    if (nextAmount !== null && nextAmount > outstanding + 0.009) {
                      setError(`Maximum payment is ${formatCurrency(outstanding)}.`);
                      return;
                    }

                    setAmountInput(nextInput);
                    setError(null);
                  }}
                  placeholder="0.00"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
                  {getActiveCompanyCurrency().code}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5 pt-1">
                <span className="text-[10px] text-muted-foreground self-center mr-1 font-medium">Quick:</span>
                {[25, 50, 100].map((percent) => (
                  <button
                    key={percent}
                    type="button"
                    onClick={() => applyPreset(percent)}
                    disabled={saving || outstanding <= 0}
                    className="text-[10px] font-semibold px-2 py-0.5 rounded-md border border-border/60 bg-background hover:bg-primary/10 hover:border-primary/30 hover:text-primary transition-colors disabled:opacity-50"
                  >
                    {percent === 100 ? "Full" : `${percent}%`}
                  </button>
                ))}
              </div>
            </div>

            {/* Date & Payment Method */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-foreground">Payment Date *</Label>
                <DatePartsPicker value={paymentDate} onChange={setPaymentDate} disabled={saving} />
              </div>

              <PaymentMethodFieldsGroup
                id="balance-payment-method"
                value={methodDraft}
                onChange={setMethodDraft}
                banks={banks}
                disabled={saving}
                showDetails={false}
                allowNone={false}
                label="Payment Method"
                labelClassName="text-xs font-semibold text-foreground"
              />
            </div>

            {/* Bank field in full single row */}
            {paymentMethodUsesBank(methodDraft.paymentMethod) && (
              <div className="space-y-1.5 min-w-0 w-full">
                <Label className="text-xs font-semibold text-foreground">Bank</Label>
                <ExpenseBankSelect
                  value={methodDraft.bankId}
                  banks={banks}
                  onChange={(v) => setMethodDraft((d) => ({ ...d, bankId: v }))}
                  disabled={saving}
                  placeholder="Search & select bank"
                  triggerClassName="w-full glass-input h-10"
                />
              </div>
            )}

            {/* Method Reference Details */}
            {methodDraft.paymentMethod === "online" && (
              <div className="space-y-1.5 min-w-0 w-full">
                <Label className="text-xs font-semibold text-foreground">Transaction reference no.</Label>
                <Input
                  className="glass-input h-10 w-full"
                  value={methodDraft.transactionReferenceNo}
                  onChange={(e) => setMethodDraft((d) => ({ ...d, transactionReferenceNo: e.target.value }))}
                  placeholder="e.g. TXN-123456"
                  disabled={saving}
                />
              </div>
            )}

            {methodDraft.paymentMethod === "cheque" && (
              <div className="space-y-1.5 min-w-0 w-full">
                <Label className="text-xs font-semibold text-foreground">Cheque number</Label>
                <Input
                  className="glass-input h-10 w-full"
                  value={methodDraft.chequeNo ?? ""}
                  onChange={(e) => setMethodDraft((d) => ({ ...d, chequeNo: e.target.value }))}
                  placeholder="e.g. 0048291"
                  disabled={saving}
                />
              </div>
            )}

            {methodDraft.paymentMethod === "pay_order" && (
              <div className="space-y-1.5 min-w-0 w-full">
                <Label className="text-xs font-semibold text-foreground">Pay order no.</Label>
                <Input
                  className="glass-input h-10 w-full"
                  value={methodDraft.payOrderNo}
                  onChange={(e) => setMethodDraft((d) => ({ ...d, payOrderNo: e.target.value }))}
                  placeholder="e.g. PO-98765"
                  disabled={saving}
                />
              </div>
            )}

            {methodDraft.paymentMethod === "cash_deposit" && (
              <div className="space-y-1.5 min-w-0 w-full">
                <Label className="text-xs font-semibold text-foreground">Deposit slip no.</Label>
                <Input
                  className="glass-input h-10 w-full"
                  value={methodDraft.depositSlipNo ?? ""}
                  onChange={(e) => setMethodDraft((d) => ({ ...d, depositSlipNo: e.target.value }))}
                  placeholder="e.g. DEP-4412"
                  disabled={saving}
                />
              </div>
            )}

            {/* Notes */}
            <div className="space-y-1.5">
              <Label htmlFor="balance-payment-notes" className="text-xs font-semibold text-foreground">
                Notes
              </Label>
              <Textarea
                id="balance-payment-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes or reference details"
                rows={2}
                disabled={saving}
              />
            </div>

            {error ? <p className="text-sm text-destructive">{error}</p> : null}
          </div>

          {/* Right Column: Live Balance Preview Card */}
          <div className="flex flex-col justify-between rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-4">
            <div>
              <div className="flex items-center gap-2 pb-3 border-b border-primary/10">
                <Calculator className="w-4 h-4 text-primary" />
                <h4 className="font-bold text-xs uppercase tracking-wider text-foreground">Live Balance Preview</h4>
              </div>

              <div className="space-y-3 pt-3 text-xs">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Outstanding balance</span>
                  <span className="font-bold text-foreground text-sm">{formatCurrency(outstanding)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">This payment</span>
                  <span className={cn("font-bold text-sm", paymentAmount > 0 ? "text-emerald-600" : "text-foreground")}>
                    {paymentAmount > 0 ? `−${formatCurrency(paymentAmount)}` : "—"}
                  </span>
                </div>
                <div className="flex justify-between items-center border-t border-primary/10 pt-2">
                  <span className="text-muted-foreground">Remaining after payment</span>
                  <div className="flex items-center gap-1.5">
                    <span className="font-extrabold text-primary text-sm">
                      {formatCurrency(remainingAfterPayment)}
                    </span>
                    {isFullySettled && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 border border-emerald-500/20">
                        <CheckCircle2 className="w-3 h-3" /> Fully Settled
                      </span>
                    )}
                  </div>
                </div>

                {paymentAmount > 0 ? (
                  <div className="bg-background/80 rounded-lg border border-primary/20 p-3 space-y-2 mt-2">
                    <div className="flex justify-between text-[11px] font-bold">
                      <span className="text-muted-foreground">Payment Coverage</span>
                      <span className="text-primary">{percentCovered}%</span>
                    </div>
                    <div className="h-2 w-full bg-primary/20 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all bg-primary"
                        style={{ width: `${percentCovered}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground italic text-center pt-0.5">
                      {isFullySettled
                        ? "This payment will settle the entire outstanding balance."
                        : `Remaining balance of ${formatCurrency(remainingAfterPayment)} will remain due.`}
                    </p>
                  </div>
                ) : (
                  <div className="bg-background/50 rounded-lg border border-border/50 p-3 text-center text-xs text-muted-foreground italic">
                    Enter payment amount to preview balance impact.
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground pt-2 border-t border-primary/10">
              <Info className="w-3.5 h-3.5 text-primary shrink-0" />
              <span>Cash/bank accounts and customer ledger are updated automatically upon submission.</span>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4 border-t border-border/50 pt-3 gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="button"
            className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-5"
            onClick={() => void save()}
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving…
              </>
            ) : (
              "Record payment"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
